import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useNavigate } from 'react-router-dom'
import {
  cancelSubscription,
  createPortalSession,
  createSetupIntent,
  getBillingSummary,
  updateAutoRefill,
  type BillingSummary,
} from '../../api/billingReader'

const stripePublicKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null
const creatorPlusPriceId = (import.meta as any).env?.VITE_STRIPE_PRICE_CREATOR_PLUS || ''
const creatorProPriceId = (import.meta as any).env?.VITE_STRIPE_PRICE_CREATOR_PRO || ''

export function resolveCreatorTierFromPriceId(selectedPriceId: string): 'creator' | 'creator_pro' {
  return selectedPriceId === creatorProPriceId ? 'creator_pro' : 'creator'
}

function resolveCurrentTierFromSummary(summary: BillingSummary | null): 'creator' | 'creator_pro' | 'none' {
  const name = String(summary?.plan?.name || '').toLowerCase()
  if (name.includes('creator pro') || name.includes('creator-pro')) return 'creator_pro'
  if (name.includes('creator')) return 'creator'
  return 'none'
}

function isCreatorPlanActive(summary: BillingSummary | null): boolean {
  const status = String(summary?.plan?.status || '').toLowerCase()
  const tier = resolveCurrentTierFromSummary(summary)
  return tier !== 'none' && (status === 'active' || status === 'trialing')
}

function SetupCardForm({
  clientSecret,
  onSaved,
  onClose,
}: {
  clientSecret: string
  onSaved: () => void
  onClose: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)
    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    })
    if (result.error) {
      setError(result.error.message || 'Failed to save card')
      setSubmitting(false)
      return
    }
    setSubmitting(false)
    onSaved()
    onClose()
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-lg border border-white/15 px-3 py-1.5 text-sm">
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !stripe || !elements}
          className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Saving...' : 'Save card'}
        </button>
      </div>
    </form>
  )
}

export default function BillingSettings() {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<BillingSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [savingAutoRefill, setSavingAutoRefill] = useState(false)
  const [cancelingPlan, setCancelingPlan] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCardModal, setShowCardModal] = useState(false)
  const [setupIntentSecret, setSetupIntentSecret] = useState<string>('')
  const [selectedPriceId, setSelectedPriceId] = useState<string>(creatorPlusPriceId || creatorProPriceId || '')
  const [autoRefillEnabled, setAutoRefillEnabled] = useState(false)
  const [thresholdPixe, setThresholdPixe] = useState('0')
  const [amountPixe, setAmountPixe] = useState('0')
  const [maxUsdPerMonth, setMaxUsdPerMonth] = useState('')

  const stripeConfigured = Boolean(stripePromise) && Boolean(summary?.capabilities?.stripe_configured ?? true)

  const refreshSummary = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getBillingSummary()
      setSummary(data)
      setAutoRefillEnabled(Boolean(data.auto_refill.enabled))
      setThresholdPixe(String(data.auto_refill.threshold_pixe || 0))
      setAmountPixe(String(data.auto_refill.amount_pixe || 0))
      setMaxUsdPerMonth(data.auto_refill.max_usd_per_month == null ? '' : String(data.auto_refill.max_usd_per_month))
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load billing.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refreshSummary()
  }, [])

  const setupIntentOptions = useMemo(
    () => ({
      clientSecret: setupIntentSecret,
      appearance: { theme: 'night' as const },
    }),
    [setupIntentSecret]
  )
  const currentTier = useMemo(() => resolveCurrentTierFromSummary(summary), [summary])
  const planOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = []
    if (creatorPlusPriceId) options.push({ value: creatorPlusPriceId, label: 'Creator Plus' })
    if (creatorProPriceId) options.push({ value: creatorProPriceId, label: 'Creator Pro' })
    return options
  }, [])
  const hasPlanOptions = planOptions.length > 0
  const selectedTier = useMemo(() => resolveCreatorTierFromPriceId(selectedPriceId), [selectedPriceId])
  const creatorPlanActive = useMemo(() => isCreatorPlanActive(summary), [summary])
  const isSameTierActive = creatorPlanActive && currentTier !== 'none' && currentTier === selectedTier
  const planCtaLabel = isSameTierActive ? 'Current plan' : creatorPlanActive ? 'Change plan' : 'Subscribe'

  const handleAutoRefillSave = async () => {
    setSavingAutoRefill(true)
    setError(null)
    try {
      await updateAutoRefill({
        enabled: autoRefillEnabled,
        threshold_pixe: Number(thresholdPixe || 0),
        amount_pixe: Number(amountPixe || 0),
        max_usd_per_month: maxUsdPerMonth.trim() ? Number(maxUsdPerMonth) : null,
      })
      await refreshSummary()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to save auto-refill settings.')
    } finally {
      setSavingAutoRefill(false)
    }
  }

  const openCardModal = async () => {
    setError(null)
    try {
      const data = await createSetupIntent()
      setSetupIntentSecret(data.client_secret)
      setShowCardModal(true)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not initialize card setup.')
    }
  }

  const openPlanCheckout = () => {
    if (isSameTierActive) {
      return
    }
    if (!selectedPriceId) {
      setError('Creator plan price IDs are not configured.')
      return
    }
    const tier = resolveCreatorTierFromPriceId(selectedPriceId)
    navigate(`/subscribe?tier=${tier}&interval=month`)
  }

  const openPortal = async () => {
    setError(null)
    try {
      const returnUrl = `${window.location.origin}/settings/billing`
      const data = await createPortalSession(returnUrl)
      window.location.assign(data.url)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not open billing portal.')
    }
  }

  const handleUnsubscribe = async () => {
    if (!creatorPlanActive || cancelingPlan) return
    const confirmed = window.confirm('Cancel your current subscription now?')
    if (!confirmed) return
    setCancelingPlan(true)
    setError(null)
    try {
      await cancelSubscription()
      await refreshSummary()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to cancel subscription.')
    } finally {
      setCancelingPlan(false)
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-extrabold">Billing</h1>

      {!stripeConfigured ? (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-200">
          Stripe not configured. Billing actions are disabled until Stripe keys are set.
        </div>
      ) : null}
      {loading ? <div className="rounded-xl border border-white/10 p-4">Loading...</div> : null}
      {error ? <div className="rounded-xl border border-red-500/40 p-4 text-sm text-red-300">{error}</div> : null}

      {summary ? (
        <div className="space-y-4">
          <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Current Plan</h2>
                <p className="text-sm text-white/70">
                  {summary.plan.name} - {summary.plan.status}
                  {summary.plan.current_period_end ? ` - renews ${new Date(summary.plan.current_period_end).toLocaleDateString()}` : ''}
                </p>
                <p className="text-sm text-white/60">${summary.plan.price_usd.toFixed(2)} / month</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                {hasPlanOptions ? (
                  <select
                    value={selectedPriceId}
                    onChange={(e) => setSelectedPriceId(e.target.value)}
                    className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm"
                  >
                    {planOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-xs text-white/55">Plan options unavailable.</div>
                )}
                <button
                  type="button"
                  disabled={!stripeConfigured || isSameTierActive || !hasPlanOptions}
                  onClick={openPlanCheckout}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {planCtaLabel}
                </button>
                {creatorPlanActive ? (
                  <button
                    type="button"
                    onClick={handleUnsubscribe}
                    disabled={cancelingPlan}
                    className="rounded-lg border border-red-500/40 px-4 py-2 text-sm font-semibold text-red-300 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {cancelingPlan ? 'Unsubscribing...' : 'Unsubscribe'}
                  </button>
                ) : null}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <h2 className="text-lg font-semibold">Auto PIXE Refill</h2>
            <p className="mb-4 text-sm text-white/70">Automatically buy PIXE when your wallet drops below your threshold.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={autoRefillEnabled} onChange={(e) => setAutoRefillEnabled(e.target.checked)} />
                Enable auto-refill
              </label>
              <div />
              <label className="text-sm">
                <span className="mb-1 block text-white/75">Threshold (PIXE)</span>
                <input
                  type="number"
                  min={0}
                  value={thresholdPixe}
                  onChange={(e) => setThresholdPixe(e.target.value)}
                  className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2"
                />
              </label>
              <label className="text-sm">
                <span className="mb-1 block text-white/75">Refill amount (PIXE)</span>
                <input
                  type="number"
                  min={0}
                  value={amountPixe}
                  onChange={(e) => setAmountPixe(e.target.value)}
                  className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2"
                />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block text-white/75">Monthly cap (USD, optional)</span>
                <input
                  type="number"
                  min={0}
                  value={maxUsdPerMonth}
                  onChange={(e) => setMaxUsdPerMonth(e.target.value)}
                  className="w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2"
                />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <div className="text-sm text-white/70">
                {summary.payment_method
                  ? `${summary.payment_method.brand.toUpperCase()} **** ${summary.payment_method.last4} exp ${summary.payment_method.exp_month}/${summary.payment_method.exp_year}`
                  : 'No saved card'}
              </div>
              <button
                type="button"
                onClick={openCardModal}
                disabled={!stripeConfigured}
                className="rounded-lg border border-white/15 px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                {summary.payment_method ? 'Change card' : 'Add card'}
              </button>
              <button
                type="button"
                onClick={handleAutoRefillSave}
                disabled={savingAutoRefill}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
              >
                {savingAutoRefill ? 'Saving...' : 'Save'}
              </button>
            </div>
          </section>

          <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <h2 className="text-lg font-semibold">Invoices & payment methods</h2>
            <p className="mb-4 text-sm text-white/70">Open Stripe billing portal to manage invoices, receipts, and cards.</p>
            <button
              type="button"
              onClick={openPortal}
              disabled={!stripeConfigured}
              className="rounded-lg border border-white/15 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              Open billing portal
            </button>
          </section>
        </div>
      ) : null}

      {showCardModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0d1118] p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add payment method</h3>
              <button type="button" onClick={() => setShowCardModal(false)} className="rounded px-2 py-1 text-sm text-white/70">
                Close
              </button>
            </div>
            {stripePromise && setupIntentSecret ? (
              <Elements stripe={stripePromise} options={setupIntentOptions}>
                <SetupCardForm clientSecret={setupIntentSecret} onSaved={() => void refreshSummary()} onClose={() => setShowCardModal(false)} />
              </Elements>
            ) : (
              <p className="text-sm text-red-300">Unable to initialize Stripe Elements.</p>
            )}
          </div>
        </div>
      ) : null}

    </div>
  )
}
